import Vue, { DirectiveBinding } from 'vue';

declare global {
  interface HTMLElement {
    focusTrapHandler?: (event: KeyboardEvent) => void;
    _focusTrapContext?: Vue;
    _focusTrapEnableArg?: string;
    _focusTrapWasEnabled?: boolean;
  }
}

// helper function to get all focusable elements in the element
export function getFocusableElements(el: HTMLElement): HTMLElement[] {

  if(!el) return [];

  const potentialElements = Array.from(
    el.querySelectorAll<HTMLElement>(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    )
  );

  return potentialElements.filter(element =>
    !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length) &&
    window.getComputedStyle(element).visibility !== 'hidden' &&
    window.getComputedStyle(element).display !== 'none' &&
    !element.hasAttribute('disabled') &&
    element.getAttribute('tabindex') !== '-1'
  );

}

// v-focus-trap Directive:
// 1. Ensures the attached element is focusable and automatically focuses it (or its first focusable child).
// 2. Traps keyboard navigation (Tab/Shift+Tab) within the element.
// 3. Calls the bound function (passed as the directive value) when the Escape (Esc) key is pressed.
//    Usage (Always Active): v-focus-trap="closeModalFunction"
// 4. Optionally accepts a directive argument (the name of a boolean data property on the component).
//    If an argument is provided, the Escape/Tab trapping logic only executes if the corresponding data property is `true` at the time of the keydown event.
//    Usage (Conditionally Active): v-focus-trap:isTrapEnabledPropertyName="closeModalFunction"
Vue.directive('focus-trap', {
  inserted(el: HTMLElement, binding: DirectiveBinding<() => void>, vnode: VNode) {
    // To add on component type in attribute - example v-focus-trap="closeModal"
    // binding.value will be for example "closeModal" function

    el._focusTrapContext = vnode.context;
    el._focusTrapEnableArg = binding.arg;

    // checks if element can be focused
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0');
    }

    //only focus on insert if the trap is enabled
    const shouldFocusOnInsert = (() => {
      const enableArg = binding.arg;
      const context = vnode.context;
      if (enableArg && context) {
        return !!context[enableArg];
      }
      return true;
    })();

    if (shouldFocusOnInsert) {
      // focus on the element or its first child
      const focusable = getFocusableElements(el);
      if (focusable.length) {
        (focusable[0] as HTMLElement).focus();
      } else {
        el.focus();
      }
    }

    // create a keydown handler for both ESC and tab-trapping
    el.focusTrapHandler = (event: KeyboardEvent) => {
      //if ESC, call the function that was passed (like closeModal)
      // if (event.key === 'Escape' || event.key === 'Esc') {
      const context = el._focusTrapContext;
      const enableArg = el._focusTrapEnableArg;

      //if an argument was provided - check the property on the component instance
      if (enableArg && context) {
        // check if the property exists and is truthy
        const isEnabled = !!context[enableArg];
        if (!isEnabled) {
          // if the condition property is false - trap innactive
          return;
        }
    }

      if ((event.key === 'Escape' || event.key === 'Esc') && !event.target.closest('.dropdown-open')) {
        if (typeof binding.value === 'function') {
          // call the close function (like "closeModal")
          binding.value();
        }
        return;
      }

      //traps focus on Tab / Shift+Tab
    if (event.key === 'Tab') {
        const focusableEls = getFocusableElements(el);
        const firstEl = focusableEls[0] as HTMLElement;
        const lastEl = focusableEls[focusableEls.length - 1] as HTMLElement;

        if (focusableEls.length === 0) return;

        // SHIFT + Tab
        if (event.shiftKey && document.activeElement === firstEl) {
          lastEl.focus();
          event.preventDefault();
      }
      // Tab (without SHIFT)
        else if (!event.shiftKey && document.activeElement === lastEl) {
          firstEl.focus();
          event.preventDefault();
      }
      }
    };

    // attaches that handler
    el.addEventListener('keydown', el.focusTrapHandler);
  },

  update(el: HTMLElement, binding: DirectiveBinding<() => void>, vnode: VNode) {
    el._focusTrapContext = vnode.context;

    const enableArg = binding.arg;
    const context = vnode.context;

    if (enableArg && context) {
      const isEnabled = !!context[enableArg];

      //only steal focus when changing from false to true
      if (isEnabled && !el._focusTrapWasEnabled) {
        const focusable = getFocusableElements(el);
        if (focusable.length) {
          (focusable[0] as HTMLElement).focus();
        } else {
          el.focus();
        }
      }
      el._focusTrapWasEnabled = isEnabled;
    }
  },

  unbind(el: HTMLElement) {
    if (el.focusTrapHandler) {
      el.removeEventListener('keydown', el.focusTrapHandler);
    }
    delete el.focusTrapHandler;
    delete el._focusTrapContext;
    delete el._focusTrapEnableArg;
    delete el._focusTrapWasEnabled;
  }
});
